import { ShieldCheck, Zap, Lock, Clock3, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Why SMART — a professional, no-fluff reassurance strip rendered on every
 * entry page (landing + join + marketplace). The copy answers "why am I
 * here, why should I trust it, what do I get?" in one visual sweep so a
 * first-time visitor who clicked a shared link doesn't bounce.
 */
export default function WhySmartBanner({ compact = false, className = "" }) {
    const pillars = [
        { icon: ShieldCheck, title: "Verified pros only",     body: "ID + address check on every Royal Pro. Police certificate for Certified tier." },
        { icon: Lock,        title: "In-app payments",         body: "No cash exchange. Card checkout + buyer protection on every booking." },
        { icon: Clock3,      title: "24 / 7 response",         body: "SOS pings, live admin + AI concierge for the moments that matter." },
        { icon: Zap,         title: "60-second booking",       body: "Pick a service, drop the address, confirm. Pro dispatched same hour." },
    ];

    if (compact) {
        return (
            <div
                data-testid="why-smart-compact"
                className={`inline-flex items-center flex-wrap gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.26em] text-muted-foreground ${className}`}
            >
                {pillars.map((p) => (
                    <span key={p.title} className="inline-flex items-center gap-1.5">
                        <p.icon size={11} className="text-[#FFD700]" />
                        {p.title}
                    </span>
                ))}
            </div>
        );
    }

    return (
        <section
            data-testid="why-smart-banner"
            className={`relative border-y border-[#FFD700]/15 bg-gradient-to-b from-[#141005] via-background to-background py-14 lg:py-20 ${className}`}
        >
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="text-center mb-10 lg:mb-14">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD700]/40 text-[10px] uppercase tracking-[0.3em] text-amber-300/90 mb-5">
                        <Sparkles size={11} /> Why SMART · the Royal standard
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground leading-[1.1]">
                        Professional. <span className="gold-text italic">Insured.</span><br className="sm:hidden" /> On time. No games.
                    </h2>
                    <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                        Jamaica's only verified on-demand network. We ID-check every pro,
                        settle every job in-app, and have a human admin on duty 24/7 — so you
                        get the service you booked, exactly how you booked it.
                    </p>
                </div>

                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="why-smart-pillars">
                    {pillars.map((p) => (
                        <div
                            key={p.title}
                            data-testid={`why-smart-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-xl border border-white/10 bg-black/30 p-5 hover:border-[#FFD700]/40 transition-colors"
                        >
                            <div className="w-10 h-10 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30 grid place-items-center mb-4">
                                <p.icon size={18} className="text-[#FFD700]" strokeWidth={1.6} />
                            </div>
                            <div className="font-serif text-lg text-foreground mb-1.5">{p.title}</div>
                            <div className="text-xs text-muted-foreground leading-relaxed">{p.body}</div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 lg:mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                    <Link to="/services" className="hover:text-[#FFD700] transition-colors" data-testid="why-smart-link-services">
                        Browse services →
                    </Link>
                    <span>·</span>
                    <Link to="/join" className="hover:text-[#FFD700] transition-colors" data-testid="why-smart-link-join">
                        Join SMART →
                    </Link>
                    <span>·</span>
                    <Link to="/vip" className="hover:text-[#FFD700] transition-colors" data-testid="why-smart-link-vip">
                        See VIP perks →
                    </Link>
                </div>
            </div>
        </section>
    );
}
